import {Component, forwardRef, Input, OnDestroy, OnInit} from '@angular/core';
import {WidgetDataRetentionAndDeletionV0HeaderDto} from '../models/widget-data-retention-and-deletion-v0-header.dto';
import {
  WidgetDataRetentionAndDeletionV0PreviewValueDto
} from '../models/widget-data-retention-and-deletion-v0-preview-value.dto';
import {WidgetUtils} from '../../../widget.utils';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validators
} from '@angular/forms';
import {takeUntil} from 'rxjs/operators';
import {of, Subject} from 'rxjs';
import {
  WidgetDataRetentionAndDeletionV0ResponseDto
} from '../models/widget-data-retention-and-deletion-v0-response.dto';
import {CustomValidator} from '../../../../utils/custom-validator';
import {WidgetDialogService} from '../../../../services/widget-dialog.service';
import {IoniconsOutline} from '@pt/pt-models';

@Component({
  selector: 'pt-widget-data-retention-and-deletion-v0-row',
  templateUrl: './widget-data-retention-and-deletion-v0-row.component.html',
  styleUrls: ['./widget-data-retention-and-deletion-v0-row.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => WidgetDataRetentionAndDeletionV0RowComponent)
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => WidgetDataRetentionAndDeletionV0RowComponent),
      multi: true
    }
  ]
})
export class WidgetDataRetentionAndDeletionV0RowComponent implements OnDestroy, OnInit, ControlValueAccessor {
  @Input() value: WidgetDataRetentionAndDeletionV0PreviewValueDto;
  @Input() header: WidgetDataRetentionAndDeletionV0HeaderDto;
  @Input() unit: WidgetDataRetentionAndDeletionV0ResponseDto[];
  @Input() bgColor: string;

  destroy$: Subject<any> = new Subject();

  id = WidgetUtils.generateId('W_DRAD');
  IconCheck = IoniconsOutline.check;
  IconClose = IoniconsOutline.close;
  formGroup: FormGroup;
  private onChanged: (val) => void;
  private onTouched: () => void;

  constructor(private widgetDialogService: WidgetDialogService) {
  }

  ngOnInit(): void {
    this.formGroup = new FormGroup({
      category: new FormControl(this.value?.category, [Validators.required]),
      retentionPeriod: new FormControl(this.value?.retentionPeriod ? this.value?.retentionPeriod : 1),
      retentionPeriodUnit: new FormControl(this.value?.retentionPeriodUnit ? this.value?.retentionPeriodUnit :
        this.unit?.length > 0 ? this.unit[0]?.uuid : null),
      retentionPeriodOther: new FormControl(this.value?.retentionPeriodOther),
      retentionPeriodReason: new FormControl(this.value?.retentionPeriodReason),
      pseudonymization: new FormControl(this.value?.pseudonymization),
      anonymization: new FormControl(this.value?.anonymization),
      deletion: new FormControl(this.value?.deletion),
      note: new FormControl(this.value?.note)
    }, [CustomValidator.widgetDataRetentionAndDeletionCheckboxLimit(1, ['pseudonymization', 'anonymization', 'deletion'])]);

    this.changeRetentionPeriodOther(this.value?.retentionPeriodOther);
    this.changeStatus(this.formGroup.getRawValue());

    this.formGroup.controls.retentionPeriodOther.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe((v) => {
        this.changeRetentionPeriodOther(v);
      }
    );
    this.formGroup?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe((v) => {
      this.changeStatus(v);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(obj: any): void {
  }

  validate(value: FormControl) {
    return this.formGroup?.invalid && {
      invalid: true
    };
  }

  openTextArea(formControlName: string, title: string) {
    const dialog = this.widgetDialogService.runTextArea(
      of(title),
      this.formGroup.controls[formControlName].value,
      true);
    dialog.subscribe((value) => {
      this.formGroup.controls[formControlName].setValue(value);
    });
  }

  private changeStatus(v) {
    if (this.onChanged) {
      this.onChanged(v);
    }
    if (this.onTouched) {
      this.onTouched();
    }

  }

  private changeRetentionPeriodOther(retentionPeriodOther: boolean) {
    if (retentionPeriodOther) {
      this.formGroup.controls.retentionPeriod.disable();
      this.formGroup.controls.retentionPeriodUnit.disable();
      this.formGroup.controls.retentionPeriodReason.setValidators([Validators.required, Validators.minLength(4)]);
      this.formGroup.controls.retentionPeriod.clearValidators();
      this.formGroup.controls.retentionPeriodUnit.clearValidators();
    } else {
      this.formGroup.controls.retentionPeriod.enable();
      this.formGroup.controls.retentionPeriodUnit.enable();
      this.formGroup.controls.retentionPeriodReason.clearValidators();
      this.formGroup.controls.retentionPeriod.setValidators([Validators.required]);
      this.formGroup.controls.retentionPeriodUnit.setValidators([Validators.required]);
    }
    this.formGroup.controls.retentionPeriod.updateValueAndValidity();
    this.formGroup.controls.retentionPeriodUnit.updateValueAndValidity();
    this.formGroup.controls.retentionPeriodReason.updateValueAndValidity();
  }
}
